import { useCallback, useEffect, useRef, useState } from "react";
import type { ConnectionState, Guidance, NodeTrace, Speaker, Turn } from "../types";

const PING_INTERVAL_MS = 20_000; // well under the ALB's idle timeout
const MAX_BACKOFF_MS = 10_000;

interface CopilotSocket {
  connection: ConnectionState;
  transcript: Turn[];
  guidance: Guidance | null;
  activeNodes: NodeTrace[];
  thinking: boolean;
  error: string | null;
  send: (speaker: Speaker, text: string) => void;
}

/**
 * Owns the WebSocket to the Copilot backend.
 *
 * Reconnection is cheap by design: conversation state lives in the backend's
 * checkpointer keyed by session id, so a dropped socket replays the full state on
 * reconnect rather than losing the conversation. That is also why the ALB needs no
 * sticky sessions - any backend task can serve any session.
 */
export function useCopilot(sessionId: string | null): CopilotSocket {
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [transcript, setTranscript] = useState<Turn[]>([]);
  const [guidance, setGuidance] = useState<Guidance | null>(null);
  const [activeNodes, setActiveNodes] = useState<NodeTrace[]>([]);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    closedByUsRef.current = false;
    let reconnectTimer: number | undefined;
    let pingTimer: number | undefined;

    const connect = () => {
      const scheme = window.location.protocol === "https:" ? "wss" : "ws";
      const socket = new WebSocket(
        `${scheme}://${window.location.host}/ws/sessions/${encodeURIComponent(sessionId)}`,
      );
      socketRef.current = socket;
      setConnection("connecting");

      socket.onopen = () => {
        attemptRef.current = 0;
        setConnection("open");
        setError(null);
        pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);
      };

      socket.onmessage = (event) => {
        const message = JSON.parse(event.data as string);
        switch (message.type) {
          case "state":
            setTranscript(message.transcript ?? []);
            setGuidance(message.guidance ?? null);
            break;
          case "turn":
            setTranscript((current) => [...current, message.turn]);
            if (message.turn.speaker === "customer") {
              setThinking(true);
              setActiveNodes([]);
            }
            break;
          case "node":
            setActiveNodes((current) => [
              ...current,
              { node: message.node, ms: message.ms ?? 0, status: message.status ?? "ok" },
            ]);
            break;
          case "guidance":
            setGuidance(message.payload);
            setThinking(false);
            break;
          case "error":
            setError(message.message);
            setThinking(false);
            break;
          case "pong":
            break;
        }
      };

      socket.onerror = () => setConnection("error");

      socket.onclose = () => {
        window.clearInterval(pingTimer);
        setThinking(false);
        if (closedByUsRef.current) {
          setConnection("closed");
          return;
        }
        setConnection("closed");
        // Exponential backoff so a backend deploy does not produce a reconnect storm.
        const delay = Math.min(500 * 2 ** attemptRef.current, MAX_BACKOFF_MS);
        attemptRef.current += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      window.clearTimeout(reconnectTimer);
      window.clearInterval(pingTimer);
      socketRef.current?.close();
    };
  }, [sessionId]);

  const send = useCallback((speaker: Speaker, text: string) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError("Not connected to the Copilot service.");
      return;
    }
    socket.send(JSON.stringify({ type: "message", speaker, text }));
  }, []);

  return { connection, transcript, guidance, activeNodes, thinking, error, send };
}
